import type { OffMapNotice } from '../model/selection.js';
import { tabLabel } from '../model/tabs.js';
import { useConsoleStore } from '../state/store.js';
import { DATA, LABEL } from '../../theme/type.js';

/**
 * `RED-INSPECTOR-1 · GEO SLOT 13 · NOT ON THIS MAP → SPACE SDA`
 *
 * The answer to *why did the board not move when I clicked that*, on whichever
 * board is asking. It began as a private function of the theatre pane; the
 * orbital board turned out to have the identical dead end pointing the other way
 * — five of the twelve contacts a BLUFOR seat holds in turn 1 are on the ground —
 * so this is the M5-D3 second use, and it is extracted rather than copied.
 *
 * What it does *not* do is decide anything. `offMapNoticeOf(resolved, tab)` owns
 * the question of whether a subject is drawn on a given board, so the two callers
 * cannot drift about what "not here" means, the same way `naturalSlotFor` keeps
 * them from drifting about what a click means.
 *
 * ## Top-left, on both boards
 *
 * It is the one corner neither canvas claims: LAYERS holds the top-right on both,
 * the renderers' LEGEND the bottom-left, their zoom column the bottom-right, and
 * a strip along the bottom edge would have had to fight two of them at the 1280
 * floor. Same corner on both boards is also the point — a notice that moved
 * between tabs would be a new thing to find rather than the same thing again.
 *
 * It is a **button, not a banner**, when the tab it names exists: the whole
 * content of the notice is "the picture you want is one tab over", and making the
 * reader carry that to the tab strip by hand is the second half of the same dead
 * end.
 */
export function OffMapStrip(props: {
  readonly notice: OffMapNotice;
  /** The board rendering it — `theatre-off-map`, `sda-off-map`. */
  readonly testId: string;
}): JSX.Element {
  const selectTab = useConsoleStore((state) => state.selectTab);
  const notice = props.notice;

  const label = (
    <>
      <span style={{ ...DATA, fontSize: 12, color: 'var(--text)' }}>{notice.id}</span>
      <span style={{ ...DATA, fontSize: 12, color: 'var(--text-2)' }}>
        {'· ' + notice.where + ' ·'}
      </span>
      <span style={LABEL}>NOT ON THIS MAP</span>
    </>
  );

  const frame = {
    position: 'absolute' as const,
    top: 12,
    left: 12,
    zIndex: 'var(--z-map-panel)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 28,
    padding: '0 12px',
    background: 'var(--surface)',
    border: '1px solid var(--border-strong)',
  };

  const tab = notice.tab;
  if (tab === null) {
    return (
      <div data-testid={props.testId} data-where={notice.where} style={frame}>
        {label}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid={props.testId}
      data-where={notice.where}
      onClick={() => selectTab(tab)}
      style={{ ...frame, cursor: 'pointer', textAlign: 'left' }}
    >
      {label}
      <span style={{ ...LABEL, color: 'var(--accent)' }}>{'→ ' + tabLabel(tab)}</span>
    </button>
  );
}
